import {z} from 'zod';

/**
 * Structural type for Firebase Timestamp.
 * Works with both client and admin SDKs without importing them.
 */
export interface FirebaseTimestamp {
  readonly seconds: number;
  readonly nanoseconds: number;
  toDate(): Date;
  toMillis(): number;
}

/**
 * Raw timestamp data as it appears in JSON serialization.
 *
 * Firebase Timestamps serialize differently depending on context:
 * - Public API: `seconds` and `nanoseconds` (getter properties)
 * - JSON.stringify / Cloud Functions: `_seconds` and `_nanoseconds` (internal)
 *
 * Official API documentation:
 * @see https://firebase.google.com/docs/reference/js/firestore_.timestamp (Client SDK)
 * @see https://googleapis.dev/nodejs/firestore/latest/Timestamp.html (Admin SDK)
 *
 * The _seconds/_nanoseconds serialization behavior is not officially documented,
 * but is a known SDK implementation detail:
 * @see https://github.com/firebase/firebase-admin-node/issues/1404
 * @see https://github.com/FirebaseExtended/reactfire/discussions/381
 */
export interface RawTimestampData {
  _seconds?: number;
  _nanoseconds?: number;
  seconds?: number;
  nanoseconds?: number;
}

/**
 * Type guard to check if value has valid timestamp number fields.
 * Exported for use by client/admin SDK-specific schemas.
 */
export function hasValidTimestampFields(
  value: Record<string, unknown>,
  secondsKey: string,
  nanosecondsKey: string,
): boolean {
  return (
    secondsKey in value &&
    typeof value[secondsKey] === 'number' &&
    nanosecondsKey in value &&
    typeof value[nanosecondsKey] === 'number'
  );
}

/**
 * Zod schema for Firebase Timestamp fields using structural validation.
 * Does not require Firebase SDK - validates based on shape.
 *
 * Handles both:
 * - Timestamp instances: public getters `seconds`/`nanoseconds`
 * - JSON serialized: internal properties `_seconds`/`_nanoseconds`
 *
 * Why both formats? When Timestamps pass through JSON.stringify() or
 * Cloud Functions request/response, the SDK's internal properties are
 * exposed instead of the public getters.
 *
 * Official API documentation:
 * @see https://firebase.google.com/docs/reference/js/firestore_.timestamp (Client SDK)
 * @see https://googleapis.dev/nodejs/firestore/latest/Timestamp.html (Admin SDK)
 *
 * Serialization behavior (not officially documented):
 * @see https://github.com/firebase/firebase-admin-node/issues/1404
 * @see https://github.com/FirebaseExtended/reactfire/discussions/381
 */
export const FirestoreTimestampSchema = z.custom<
  FirebaseTimestamp | RawTimestampData
>(
  (value): value is FirebaseTimestamp | RawTimestampData => {
    if (typeof value !== 'object' || value === null) {
      return false;
    }
    const obj = value as Record<string, unknown>;
    // Handle JSON-serialized timestamp (internal properties exposed)
    // See: https://github.com/firebase/firebase-admin-node/issues/1404
    if (hasValidTimestampFields(obj, '_seconds', '_nanoseconds')) {
      return true;
    }
    // Handle Timestamp instance or plain object (public getters)
    if (hasValidTimestampFields(obj, 'seconds', 'nanoseconds')) {
      return true;
    }
    return false;
  },
  {
    message:
      'Expected Firebase Timestamp (object with numeric seconds/nanoseconds or _seconds/_nanoseconds)',
  },
);

/**
 * Helper to extract seconds from a timestamp value.
 */
export function getTimestampSeconds(
  value: FirebaseTimestamp | RawTimestampData,
): number {
  if ('_seconds' in value && value._seconds !== undefined) {
    return value._seconds;
  }
  if (value.seconds !== undefined) {
    return value.seconds;
  }
  throw new Error(
    'Timestamp missing seconds field. Expected either { seconds, nanoseconds } ' +
      '(SDK getter format) or { _seconds, _nanoseconds } (JSON serialization format).',
  );
}

/**
 * Helper to extract nanoseconds from a timestamp value.
 */
export function getTimestampNanoseconds(
  value: FirebaseTimestamp | RawTimestampData,
): number {
  if ('_nanoseconds' in value && value._nanoseconds !== undefined) {
    return value._nanoseconds;
  }
  if (value.nanoseconds !== undefined) {
    return value.nanoseconds;
  }
  throw new Error(
    'Timestamp missing nanoseconds field. Expected either { seconds, nanoseconds } ' +
      '(SDK getter format) or { _seconds, _nanoseconds } (JSON serialization format).',
  );
}

/**
 * Helper to extract a timestamp's value as milliseconds since epoch.
 * Prefers the SDK's `toMillis()` accessor when available; otherwise
 * reconstructs from seconds + nanoseconds (handling both the
 * `seconds`/`nanoseconds` getter shape and the
 * `_seconds`/`_nanoseconds` JSON-serialization shape).
 *
 * @throws If the value has neither `toMillis` nor a recognizable
 *   seconds/nanoseconds pair. Callers that need to handle malformed
 *   data without crashing should wrap in try/catch.
 */
export function getTimestampMillis(
  value: FirebaseTimestamp | RawTimestampData,
): number {
  if ('toMillis' in value && typeof value.toMillis === 'function') {
    return value.toMillis();
  }
  const seconds = getTimestampSeconds(value);
  const nanoseconds = getTimestampNanoseconds(value);
  return seconds * 1000 + Math.floor(nanoseconds / 1_000_000);
}

// ============================================================================
// Converter Options
// ============================================================================

/**
 * Options for Firestore data converters.
 * Both flags are required - no implicit defaults.
 */
export interface ConverterOptions {
  /** Validate data against schema when reading from Firestore */
  validateOnRead: boolean;
  /** Validate data against schema when writing to Firestore */
  validateOnWrite: boolean;
  /**
   * Optional fields to add when writing (e.g. latestSubmission: serverTimestamp).
   * Each value is a getter called at write time.
   */
  augmentOnWrite?: Record<string, () => unknown>;
}

// ============================================================================
// Timestamp Schema Factory
// ============================================================================

/**
 * Creates a Zod schema that validates and transforms timestamp data to SDK
 * Timestamp instances. Handles both Timestamp instances and raw JSON data
 * from serialization.
 *
 * Used by client and admin SDK entry points to avoid duplicating ~30 lines
 * of validation + transform logic.
 *
 * Official API:
 * @see https://firebase.google.com/docs/reference/js/firestore_.timestamp (Client SDK)
 * @see https://googleapis.dev/nodejs/firestore/latest/Timestamp.html (Admin SDK)
 *
 * Serialization behavior (not officially documented):
 * @see https://github.com/firebase/firebase-admin-node/issues/1404
 */
export function createTimestampSchema<T extends FirebaseTimestamp>({
  TimestampClass,
  isInstance,
}: {
  TimestampClass: new (seconds: number, nanoseconds: number) => T;
  isInstance: (value: unknown) => value is T;
}) {
  return z
    .custom<T | FirebaseTimestamp | RawTimestampData>(
      (value): value is T | FirebaseTimestamp | RawTimestampData => {
        if (isInstance(value)) {
          return true;
        }
        if (typeof value !== 'object' || value === null) {
          return false;
        }
        const obj = value as Record<string, unknown>;
        if (hasValidTimestampFields(obj, '_seconds', '_nanoseconds')) {
          return true;
        }
        if (hasValidTimestampFields(obj, 'seconds', 'nanoseconds')) {
          return true;
        }
        return false;
      },
      {message: 'Expected Firebase Timestamp'},
    )
    .transform((value): T => {
      if (isInstance(value)) {
        return value;
      }
      const seconds = getTimestampSeconds(value);
      const nanoseconds = getTimestampNanoseconds(value);
      return new TimestampClass(seconds, nanoseconds);
    });
}

/**
 * A single Firestore document id, as a callable receives one to build a path:
 * non-empty, free of `/`, and not `.` or `..`, which Firestore rejects as ids.
 */
export const FirestoreDocumentIdSchema = z
  .string()
  .min(1)
  .regex(/^(?!\.\.?$)[^/]+$/, 'Must be a single document id.');
